/**
 * O que faz: reúne os hooks React Query usados pela central de Disparos.
 * Onde usa: página /admin/disparos e seus componentes.
 * Conexões: Edge Functions meta-whatsapp-* e RPC get_metricas_disparos_oficial.
 * Tabelas: whatsapp_templates_oficial (SELECT); demais operações passam por Edge Functions.
 * Data: 10/07/2026
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ErroEnvioWhatsAppOficial, interpretarErroEnvioOficial } from '@/lib/whatsapp-oficial/mensagemErroEnvioOficial';

export interface ConfigApiOficial {
  waba_id: string;
  phone_number_id: string;
  numero_exibicao?: string | null;
  ativo: boolean;
  kanban_etapa_padrao_id?: string | null;
  kanban_automatico: boolean;
  token?: string;
  modo_conexao?: 'manual' | 'incorporado_padrao' | 'coexistencia';
  onboarding_status?: 'nao_iniciado' | 'conectando' | 'conectado' | 'erro';
  historico_status?: 'nao_solicitado' | 'solicitado' | 'sincronizando' | 'sincronizado' | 'recusado';
  recebimento_oficial_status?: 'desligado' | 'sombra' | 'ativo';
  conectado_em?: string | null;
}

export interface ConfiguracaoPublicaEmbeddedSignup {
  enabled: boolean;
  app_id: string;
  configuration_id: string;
  api_version: string;
}

export interface RespostaConfigApiOficial {
  config: ConfigApiOficial | null;
  token_configurado: boolean;
  tentativa_id?: string | null;
}

export interface IniciarOnboardingPayload {
  clinicaId: string;
  modo: 'incorporado_padrao' | 'coexistencia';
  numeroEsperado: string;
  compartilharHistorico: boolean;
}

export interface ConcluirOnboardingPayload {
  clinicaId: string;
  tentativaId: string;
  state: string;
  code: string;
  wabaId: string;
  phoneNumberId: string | null;
  numeroEsperado: string;
  pin: string;
}

export interface ResultadoOnboardingValidado {
  tentativa_id: string;
  status: 'validada';
  waba_id: string;
  phone_number_id: string;
  numero_exibicao: string | null;
}

export interface TemplateOficial {
  id: string;
  nome: string;
  idioma: string;
  categoria: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: string;
  componentes: Array<{ type: string; text?: string }>;
  motivo_rejeicao: string | null;
  qualidade: string | null;
  updated_at: string;
  mapeamento_variaveis?: string[];
  texto_opt_in?: string | null;
  texto_opt_out?: string | null;
}

export interface MetricasDisparos {
  total_disparado: number;
  total_enviado: number;
  total_entregue: number;
  total_lido: number;
  total_falhou: number;
  custo_total_centavos: number;
}

export const useConfigApiOficial = (clinicaId?: string) => useQuery({
  queryKey: ['whatsapp-oficial-config', clinicaId],
  enabled: Boolean(clinicaId),
  queryFn: async () => {
    const { data, error } = await supabase.functions.invoke('meta-whatsapp-config', {
      body: { acao: 'obter', clinica_id: clinicaId },
    });
    if (error || data?.error) throw new Error(data?.error ?? 'Erro ao carregar a configuração');
    return data as RespostaConfigApiOficial;
  },
});

export const useConfiguracaoEmbeddedSignup = () => useQuery({
  queryKey: ['whatsapp-embedded-signup-configuracao-publica'],
  queryFn: async () => {
    // Edge Function: meta-whatsapp-onboarding | Operação: leitura da configuração pública.
    // Campos: enabled, app_id, configuration_id e api_version; a sessão é validada no servidor.
    // Risco: IDs públicos não autorizam a Meta; token e App Secret permanecem fora do navegador.
    const { data, error } = await supabase.functions.invoke('meta-whatsapp-onboarding', {
      body: { acao: 'configuracao_publica' },
    });
    if (error || data?.error) throw new Error(data?.error ?? 'Não foi possível carregar a conexão com a Meta');
    return data as ConfiguracaoPublicaEmbeddedSignup;
  },
  staleTime: 30 * 60 * 1000,
});

async function executarOnboardingEdgeFunction(
  corpo: Record<string, unknown>,
  fallback: string,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke('meta-whatsapp-onboarding', { body: corpo });
  if (error || (data as { error?: string } | null)?.error) {
    throw new Error((data as { error?: string } | null)?.error ?? error?.message ?? fallback);
  }
  return data as Record<string, unknown>;
}

export const useIniciarEmbeddedSignup = () => useMutation({
  mutationFn: async (payload: IniciarOnboardingPayload) => {
    // Edge Function: meta-whatsapp-onboarding | Operação: iniciar (guarda só o hash do state).
    const data = await executarOnboardingEdgeFunction({
      acao: 'iniciar',
      clinica_id: payload.clinicaId,
      modo: payload.modo,
      numero_esperado: payload.numeroEsperado,
      compartilhar_historico: payload.compartilharHistorico,
    }, 'Não foi possível iniciar a conexão com a Meta');
    return data as { tentativa_id: string; state: string; expira_em: string };
  },
});

export const useConcluirEmbeddedSignup = () => useMutation({
  mutationFn: async (payload: ConcluirOnboardingPayload) => {
    // Edge Function: meta-whatsapp-onboarding | Operação: concluir.
    // Risco: o code é de uso único e nunca é salvo pela UI; o PIN é transitório.
    const data = await executarOnboardingEdgeFunction({
      acao: 'concluir',
      clinica_id: payload.clinicaId,
      tentativa_id: payload.tentativaId,
      state: payload.state,
      code: payload.code,
      waba_id: payload.wabaId,
      phone_number_id: payload.phoneNumberId,
      numero_esperado: payload.numeroEsperado,
      pin: payload.pin,
    }, 'Não foi possível concluir a conexão');
    return data as unknown as ResultadoOnboardingValidado;
  },
});

export const usePromoverSombraEmbeddedSignup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { clinicaId: string; tentativaId: string }) => {
      // Edge Function: meta-whatsapp-onboarding | Operação: promover_sombra (ativo permanece false).
      return executarOnboardingEdgeFunction({
        acao: 'promover_sombra',
        clinica_id: payload.clinicaId,
        tentativa_id: payload.tentativaId,
      }, 'Não foi possível preparar o modo sombra');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-oficial-config', variables.clinicaId] });
    },
  });
};

export const useAtivarEmbeddedSignup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { clinicaId: string; tentativaId: string }) => {
      // Edge Function: meta-whatsapp-onboarding | Operação: ativar, com confirmação explícita.
      return executarOnboardingEdgeFunction({
        acao: 'ativar',
        clinica_id: payload.clinicaId,
        tentativa_id: payload.tentativaId,
        confirmar_ativacao: true,
      }, 'Não foi possível ativar as mensagens oficiais');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-oficial-config', variables.clinicaId] });
    },
  });
};

export const useRollbackEmbeddedSignup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { clinicaId: string }) => {
      // Edge Function: meta-whatsapp-onboarding | Operação: rollback (não apaga credenciais Uazapi).
      return executarOnboardingEdgeFunction({
        acao: 'rollback',
        clinica_id: payload.clinicaId,
      }, 'Não foi possível desativar a conexão oficial');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-oficial-config', variables.clinicaId] });
    },
  });
};

export const useSalvarConfigApiOficial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicaId, config }: { clinicaId: string; config: ConfigApiOficial }) => {
      const { data, error } = await supabase.functions.invoke('meta-whatsapp-config', {
        body: { acao: 'salvar', clinica_id: clinicaId, config },
      });
      if (error || data?.error) throw new Error(data?.error ?? 'Erro ao salvar a configuração');
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-oficial-config', variables.clinicaId] });
    },
  });
};

export const useTestarConexaoApiOficial = () => useMutation({
  mutationFn: async (clinicaId: string) => {
    const { data, error } = await supabase.functions.invoke('meta-whatsapp-config', {
      body: { acao: 'testar', clinica_id: clinicaId },
    });
    if (error || data?.error) throw new Error(data?.error ?? 'Falha no teste de conexão');
    return data.numero as {
      verified_name?: string;
      display_phone_number?: string;
      quality_rating?: string;
      code_verification_status?: string;
      messaging_limit_tier?: string;
      throughput?: unknown;
    };
  },
});

export const useTemplatesOficial = (clinicaId?: string) => useQuery({
  queryKey: ['whatsapp-oficial-templates', clinicaId],
  enabled: Boolean(clinicaId),
  queryFn: async () => {
    // Tabela: whatsapp_templates_oficial | Operação: SELECT | Filtro: clinica_id.
    // Risco: nenhum token é retornado; a RLS mantém cada clínica isolada.
    const { data, error } = await supabase
      .from('whatsapp_templates_oficial')
      .select('id, nome, idioma, categoria, status, componentes, motivo_rejeicao, qualidade, updated_at, mapeamento_variaveis, texto_opt_in, texto_opt_out')
      .eq('clinica_id', clinicaId!)
      .order('updated_at', { ascending: false });
    if (error) throw new Error('Erro ao carregar templates');
    return (data ?? []) as TemplateOficial[];
  },
});

async function corpoErroEdgeFunction(
  error: unknown,
  data: Record<string, unknown> | null,
): Promise<unknown> {
  // Motivo: em status != 2xx o client do Supabase preenche `error` e às vezes deixa `data` vazio.
  // Consequência se falhar: o toast mostra inglês técnico em vez da causa (pagamento, permissão, etc.).
  if (data && typeof data === 'object' && (typeof data.error === 'string' || typeof data.tipo === 'string')) {
    return data
  }
  const contexto = (error as { context?: Response } | null)?.context;
  if (contexto && typeof contexto.json === 'function') {
    try {
      return await contexto.json()
    } catch {
      // Mantém o fallback abaixo.
    }
  }
  if (error instanceof Error && error.message.trim()) return error
  return { message: 'Não foi possível enviar agora.' }
}

async function mensagemErroEdgeFunction(
  error: unknown,
  data: { error?: string } | null,
  fallback: string,
): Promise<string> {
  const corpo = await corpoErroEdgeFunction(error, data as Record<string, unknown> | null)
  if (corpo && typeof corpo === 'object' && typeof (corpo as { error?: string }).error === 'string') {
    const texto = (corpo as { error: string }).error.trim()
    if (texto) return texto
  }
  if (error instanceof Error && error.message.trim()) return error.message
  return fallback
}

export const useAcaoTemplate = (clinicaId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { acao: 'sincronizar' | 'criar' | 'apagar' | 'salvar_mapeamento'; template?: unknown }) => {
      const { data, error } = await supabase.functions.invoke('meta-whatsapp-templates', {
        body: { ...payload, clinica_id: clinicaId },
      });
      if (error || data?.error) {
        throw new Error(await mensagemErroEdgeFunction(error, data, 'Erro ao processar template'));
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-oficial-templates', clinicaId] });
    },
  });
};

export const useMetricasDisparos = (inicio: Date, fim: Date, clinicaId?: string | null) => useQuery({
  queryKey: ['whatsapp-oficial-metricas', inicio.toISOString(), fim.toISOString(), clinicaId ?? 'todas'],
  queryFn: async () => {
    // RPC: get_metricas_disparos_oficial | Operação: SELECT agregado.
    // Filtros: intervalo e clínica; a própria RPC ignora clínica alheia para usuário comum.
    const { data, error } = await supabase.rpc('get_metricas_disparos_oficial', {
      p_inicio: inicio.toISOString(),
      p_fim: fim.toISOString(),
      p_clinica_id: clinicaId ?? null,
    });
    if (error) throw new Error('Erro ao carregar métricas de disparos');
    return (data?.[0] ?? {
      total_disparado: 0,
      total_enviado: 0,
      total_entregue: 0,
      total_lido: 0,
      total_falhou: 0,
      custo_total_centavos: 0,
    }) as MetricasDisparos;
  },
});

export const useEnviarDisparoTeste = () => useMutation({
  mutationFn: async (payload: {
    clinica_id: string;
    telefone: string;
    template_nome: string;
    template_idioma?: string;
    variaveis?: string[];
  }) => {
    const { data, error } = await supabase.functions.invoke('meta-whatsapp-send', { body: payload });
    if (error || data?.error) {
      const corpo = await corpoErroEdgeFunction(error, data as Record<string, unknown> | null);
      throw new ErroEnvioWhatsAppOficial(interpretarErroEnvioOficial(corpo));
    }
    return data as { ok: true; wamid?: string };
  },
});
